import { Role, RoleGroup } from '../types/commission';

/** Display names for position groups. */
export const roleGroupLabels: Record<RoleGroup, string> = {
  Production: 'Loan Officers',
  Leadership: 'Leadership',
  Operations: 'Operations',
};

/**
 * Every compensation plan in the org, expressed against one shared shape so the
 * dashboard can render any of them.
 */
export const roles: Role[] = [
  {
    id: 'loan-partner',
    name: 'Loan Partner',
    group: 'Production',
    model: 'bps',
    scope: 'Personal',
    unitNoun: 'funded loan',
    unitNounPlural: 'funded loans',
    baseBps: 60,
    requiresScorecard: true,
    steps: [
      { minUnits: 0, maxUnits: 3, bonusBps: 0, totalBps: 60 },
      { minUnits: 4, maxUnits: 5, bonusBps: 5, totalBps: 65 },
      { minUnits: 6, maxUnits: 9, bonusBps: 20, totalBps: 80 },
      { minUnits: 10, maxUnits: null, bonusBps: 30, totalBps: 90 },
    ],
    promotion: {
      toRoleName: 'Loan Officer',
      logic: 'and',
      requirements: [
        { label: 'State education courses', value: 3, goal: 4, suffix: 'courses' },
        { label: 'State licenses obtained', value: 1, goal: 2, suffix: 'licenses' },
      ],
    },
    monthlyUnits: { '2026-08': 3, '2026-07': 5, '2026-06': 4, '2026-05': 2 },
  },
  {
    id: 'loan-officer',
    name: 'Loan Officer',
    group: 'Production',
    model: 'bps',
    scope: 'Personal',
    unitNoun: 'funded loan',
    unitNounPlural: 'funded loans',
    baseBps: 80,
    requiresScorecard: true,
    steps: [
      { minUnits: 0, maxUnits: 3, bonusBps: 0, totalBps: 80 },
      { minUnits: 4, maxUnits: 5, bonusBps: 5, totalBps: 85 },
      { minUnits: 6, maxUnits: 9, bonusBps: 20, totalBps: 100 },
      { minUnits: 10, maxUnits: null, bonusBps: 30, totalBps: 110 },
    ],
    promotion: {
      toRoleName: 'Senior Loan Officer',
      logic: 'or',
      requirements: [
        { label: '2 years tenure', value: 16, goal: 24, suffix: 'mo' },
        { label: '16 units in any 4 months', value: 13, goal: 16, suffix: 'units', qualifyingMonths: 4 },
      ],
    },
    monthlyUnits: { '2026-08': 5, '2026-07': 8, '2026-06': 6, '2026-05': 5 },
  },
  {
    id: 'senior-loan-officer',
    name: 'Senior Loan Officer',
    group: 'Production',
    model: 'bps',
    scope: 'Personal',
    unitNoun: 'funded loan',
    unitNounPlural: 'funded loans',
    baseBps: 85,
    requiresScorecard: true,
    steps: [
      { minUnits: 0, maxUnits: 3, bonusBps: 0, totalBps: 85 },
      { minUnits: 4, maxUnits: 5, bonusBps: 5, totalBps: 90 },
      { minUnits: 6, maxUnits: 9, bonusBps: 20, totalBps: 105 },
      { minUnits: 10, maxUnits: null, bonusBps: 30, totalBps: 115 },
    ],
    promotion: {
      toRoleName: 'Executive Loan Officer',
      logic: 'and',
      requirements: [
        { label: '1 year as Senior Loan Officer', value: 14, goal: 12, suffix: 'mo' },
        { label: '26 units in a 6-month period', value: 21, goal: 26, suffix: 'units', qualifyingMonths: 6 },
      ],
    },
    monthlyUnits: { '2026-08': 7, '2026-07': 6, '2026-06': 4, '2026-05': 3 },
  },
  {
    id: 'executive-loan-officer',
    name: 'Executive Loan Officer',
    group: 'Production',
    model: 'bps',
    scope: 'Personal',
    unitNoun: 'funded loan',
    unitNounPlural: 'funded loans',
    baseBps: 90,
    requiresScorecard: true,
    steps: [
      { minUnits: 0, maxUnits: 3, bonusBps: 0, totalBps: 90 },
      { minUnits: 4, maxUnits: 5, bonusBps: 5, totalBps: 95 },
      { minUnits: 6, maxUnits: 9, bonusBps: 20, totalBps: 110 },
      { minUnits: 10, maxUnits: null, bonusBps: 30, totalBps: 120 },
    ],
    monthlyUnits: { '2026-08': 9, '2026-07': 12, '2026-06': 10, '2026-05': 8 },
  },
  {
    id: 'developmental-team-lead',
    name: 'Developmental Team Lead',
    group: 'Leadership',
    model: 'bps',
    scope: 'Team',
    unitNoun: 'team closed unit',
    unitNounPlural: 'team closed units',
    baseBps: 60,
    requiresScorecard: true,
    steps: [
      { minUnits: 0, maxUnits: 7, bonusBps: 0, totalBps: 60 },
      { minUnits: 8, maxUnits: 11, bonusBps: 5, totalBps: 65 },
      { minUnits: 12, maxUnits: null, bonusBps: 10, totalBps: 70 },
    ],
    promotion: {
      toRoleName: 'Senior Team Lead',
      logic: 'and',
      requirements: [{ label: '12+ team units for 3 straight months', value: 2, goal: 3, suffix: 'mo' }],
    },
    monthlyUnits: { '2026-08': 9, '2026-07': 13, '2026-06': 10, '2026-05': 8 },
  },
  {
    id: 'senior-team-lead',
    name: 'Senior Team Lead',
    group: 'Leadership',
    model: 'bps',
    scope: 'Team',
    unitNoun: 'team closed unit',
    unitNounPlural: 'team closed units',
    baseBps: 40,
    requiresScorecard: true,
    steps: [
      { minUnits: 0, maxUnits: 11, bonusBps: 0, totalBps: 40 },
      { minUnits: 12, maxUnits: 16, bonusBps: 5, totalBps: 45 },
      { minUnits: 17, maxUnits: 24, bonusBps: 10, totalBps: 50 },
      { minUnits: 25, maxUnits: null, bonusBps: 15, totalBps: 55 },
    ],
    monthlyUnits: { '2026-08': 14, '2026-07': 19, '2026-06': 16, '2026-05': 12 },
  },
  {
    id: 'division-lead',
    name: 'Division Lead',
    group: 'Leadership',
    model: 'bps',
    scope: 'Division',
    unitNoun: 'division closed unit',
    unitNounPlural: 'division closed units',
    baseBps: 15,
    requiresScorecard: false,
    steps: [
      { minUnits: 0, maxUnits: 49, bonusBps: 0, totalBps: 15 },
      { minUnits: 50, maxUnits: 64, bonusBps: 1, totalBps: 16 },
      { minUnits: 65, maxUnits: 79, bonusBps: 2, totalBps: 17 },
      { minUnits: 80, maxUnits: null, bonusBps: 4, totalBps: 19 },
    ],
    monthlyUnits: { '2026-08': 58, '2026-07': 71, '2026-06': 63, '2026-05': 52 },
  },
  {
    id: 'loan-processor',
    name: 'Loan Processor',
    group: 'Operations',
    model: 'per-unit',
    scope: 'Personal',
    unitNoun: 'closed loan',
    unitNounPlural: 'closed loans',
    incentiveNoun: 'unit',
    requiresScorecard: false,
    steps: [
      { minUnits: 0, maxUnits: 9, perUnit: 0 },
      { minUnits: 10, maxUnits: 14, perUnit: 15 },
      { minUnits: 15, maxUnits: 19, perUnit: 25 },
      { minUnits: 20, maxUnits: 24, perUnit: 35 },
      { minUnits: 25, maxUnits: null, perUnit: 45 },
    ],
    promotion: {
      toRoleName: 'Senior Loan Processor',
      logic: 'and',
      requirements: [{ label: '20+ closings for 3 straight months', value: 1, goal: 3, suffix: 'mo' }],
    },
    monthlyUnits: { '2026-08': 12, '2026-07': 17, '2026-06': 21, '2026-05': 9 },
  },
  {
    id: 'senior-loan-processor',
    name: 'Senior Loan Processor',
    group: 'Operations',
    model: 'per-unit',
    scope: 'Personal',
    unitNoun: 'closed loan',
    unitNounPlural: 'closed loans',
    incentiveNoun: 'unit',
    requiresScorecard: false,
    steps: [
      { minUnits: 0, maxUnits: 9, perUnit: 0 },
      { minUnits: 10, maxUnits: 14, perUnit: 25 },
      { minUnits: 15, maxUnits: 19, perUnit: 35 },
      { minUnits: 20, maxUnits: 24, perUnit: 45 },
      { minUnits: 25, maxUnits: null, perUnit: 55 },
    ],
    monthlyUnits: { '2026-08': 16, '2026-07': 22, '2026-06': 19, '2026-05': 14 },
  },
  {
    id: 'processing-admin',
    name: 'Processing Admin',
    group: 'Operations',
    model: 'per-unit',
    scope: 'Personal',
    unitNoun: 'closing',
    unitNounPlural: 'closings',
    incentiveNoun: 'closing',
    requiresScorecard: false,
    steps: [
      { minUnits: 0, maxUnits: 19, perUnit: 0 },
      { minUnits: 20, maxUnits: 29, perUnit: 10 },
      { minUnits: 30, maxUnits: 39, perUnit: 15 },
      { minUnits: 40, maxUnits: null, perUnit: 20 },
    ],
    monthlyUnits: { '2026-08': 27, '2026-07': 34, '2026-06': 31, '2026-05': 22 },
  },
  {
    id: 'loan-execution-manager',
    name: 'Loan Execution Manager',
    group: 'Operations',
    model: 'per-unit',
    scope: 'Company',
    unitNoun: 'company closed file',
    unitNounPlural: 'company closed files',
    incentiveNoun: 'file',
    requiresScorecard: false,
    steps: [
      { minUnits: 0, maxUnits: 49, perUnit: 0 },
      { minUnits: 50, maxUnits: 59, perUnit: 10 },
      { minUnits: 60, maxUnits: 79, perUnit: 15 },
      { minUnits: 80, maxUnits: null, perUnit: 20 },
    ],
    monthlyUnits: { '2026-08': 64, '2026-07': 83, '2026-06': 71, '2026-05': 56 },
  },
  {
    id: 'avp-processing',
    name: 'AVP of Processing',
    group: 'Operations',
    model: 'per-unit',
    scope: 'Team',
    unitNoun: 'closed loan',
    unitNounPlural: 'closed loans',
    incentiveNoun: 'loan',
    requiresScorecard: false,
    monthlyCap: 5500,
    steps: [
      { minUnits: 0, maxUnits: 30, perUnit: 0 },
      { minUnits: 31, maxUnits: 35, perUnit: 40 },
      { minUnits: 36, maxUnits: 40, perUnit: 50 },
      { minUnits: 41, maxUnits: 45, perUnit: 60 },
      { minUnits: 46, maxUnits: 60, perUnit: 65 },
      { minUnits: 61, maxUnits: null, perUnit: 70 },
    ],
    monthlyUnits: { '2026-08': 38, '2026-07': 47, '2026-06': 44, '2026-05': 33 },
  },
];
